import React, { useEffect, useState } from "react";
import { Close } from "../Icons";
import { AlertTriangle, Info } from "lucide-react";
import { CustomBlueSelect } from "../CustomBlueSelect";
import {
	englishProficiencyDetails,
	gradingSystem,
	passingYear,
	pastQualification,
	schoolBoard,
	streamOptions,
	qualificationOptions,
} from "../Helper";

import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
	setCourseAcademicDetails,
	setCourseEnglishProficiencyDetails,
} from "@/store/feature/courses/courseSlice";
import { useAuth } from "@/hook/useAuth";
import { saveEligibilityDetails } from "@/store/feature/ai-course-finder/aiCourseApi";
import { saveEligibilityAnonymous } from "@/store/feature/ai-course-finder/aiCourseFinderSlice";
import { capitalFirstLetter } from "@/utils/capitalFirstLetter";
import { convertToPercentage } from "@/utils/conversionFun";
import { areObjectsEqual } from "@/utils/deepEqual";
import SelectInput from "./SelectInput";

type StreamOption = {
	id: number;
	title: string;
};

interface ErrorType {
	[key: string]: string; // Keys are dynamic field names, values are error messages
}

const CheckEligibilityModal = ({
	setIsOpen,
	isOpen,
	setEligibilityCriteriaPopUp,
	country,
}: {
	setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
	setEligibilityCriteriaPopUp: React.Dispatch<React.SetStateAction<boolean>>;
	country: string;
	isOpen: boolean;
}) => {
	const dispatch = useAppDispatch();

	// Get course details and user details from Redux store
	const courseDetails = useAppSelector((state) => state.course);
	const { userDetails } = useAppSelector((state) => state.userProfile);
	const { isAnonymous } = useAuth();

	const [errors, setErrors] = useState<ErrorType>({});
	const [allValue, setAllValue] = useState(false);
	const { savedEligibility } = useAppSelector((state) => state.aiCourseFinder);

	// Add null check for selectedTestType with fallback, but store locally
	const initialTestType = courseDetails?.englishProficiencyDetails?.test || "";
	const [selectedTestType, setSelectedTestType] = useState(initialTestType);

	// Initialize with values from courseDetails, falling back to userDetails if available
	const [userEnglishProficiencyDetails, setUserEnglishProficiencyDetails] =
		useState({
			writingScore:
				courseDetails?.englishProficiencyDetails?.writingScore || "",
			readingScore:
				courseDetails?.englishProficiencyDetails?.readingScore || "",
			listeningScore:
				courseDetails?.englishProficiencyDetails?.listeningScore || "",
			speakingScore:
				courseDetails?.englishProficiencyDetails?.speakingScore || "",
			overallScore:
				courseDetails?.englishProficiencyDetails?.overallScore || "",
			selectBoard: courseDetails?.englishProficiencyDetails?.selectBoard || "",
			englishMarks:
				courseDetails?.englishProficiencyDetails?.englishMarks || "",
			test: initialTestType,
		});

	const [
		initialEnglishProficiencyDetails,
		setInitialEnglishProficiencyDetails,
	] = useState<any>(null);
	const [hasChanged, setHasChanged] = useState(false);

	const [selectedStream, setSelectedStream] = useState<StreamOption[]>([]);

	useEffect(() => {
		if (!isOpen) return;
		
	}, [country, isOpen, courseDetails]);

	// Initialize academic details with fallbacks to user details if available
	const [academicDetails, setAcademicDetails] = useState({
		pastQualification: courseDetails?.academicDetails?.pastQualification || "",
		stream: courseDetails?.academicDetails?.stream || "",
		passingYear: courseDetails?.academicDetails?.passingYear || "",
		gradingSystem: courseDetails?.academicDetails?.gradingSystem
			? courseDetails?.academicDetails?.gradingSystem
			: "Percentage",
		pastGrades: courseDetails?.academicDetails?.pastGrades || "",
		pastMathsGrades: courseDetails?.academicDetails?.pastMathsGrades || "",
		pastChemistryGrades:
			courseDetails?.academicDetails?.pastChemistryGrades || "",
		pastPhysicsGrades: courseDetails?.academicDetails?.pastPhysicsGrades || "",
		pastEnglishGrades: courseDetails?.academicDetails?.pastEnglishGrades || "",
		board: courseDetails?.academicDetails?.selectBoard || "",
	});

	const gradingSystemLimits: { [key: string]: { min: number; max: number } } = {
		Percentage: { min: 1, max: 100 },
	};

	const onSelectHandler = (selectedValue: string) => {
		// Clear previous errors
		setErrors({
			writingScore: "",
			readingScore: "",
			listeningScore: "",
			speakingScore: "",
			overallScore: "",
		});

		// Update strictly local state for the test type instead of dispatching to Redux immediately
		const formattedTest = selectedValue.replace(/\s+/g, "-");
		setSelectedTestType(formattedTest);

		// Reset form values
		setUserEnglishProficiencyDetails({
			writingScore: "",
			readingScore: "",
			listeningScore: "",
			speakingScore: "",
			overallScore: "",
			selectBoard: "",
			englishMarks: "",
			test: formattedTest,
		});
		setAllValue(false);
		setHasChanged(true);
	};

	useEffect(() => {
		if (isOpen) {
			document.body.classList.add("!overflow-hidden");
		}

		return () => {
			document.body.classList.remove("!overflow-hidden");
		};
	}, [isOpen]);

	const onChangeHandler = (selectedValue: string, fieldName: string) => {
		// Clear specific field error
		setErrors((prev) => ({
			...prev,
			[fieldName]: "", // Clear error for the selected field
		}));

		if (fieldName === "pastGrades") {
			setAcademicDetails((prev) => ({ ...prev, [fieldName]: selectedValue }));
		} else {
			setAcademicDetails((prev) => ({
				...prev,
				[fieldName]: selectedValue,
				pastGrades: "",
			}));
		}
	};

	const onChangeInputHandler = (selectedValue: string, fieldName: string) => {
		// Clear only the specific field's error
		setErrors((prev) => ({
			...prev,
			[fieldName.toLowerCase().includes("score") ? fieldName : "overallScore"]:
				"",
		}));

		setUserEnglishProficiencyDetails((prev) => ({
			...prev,
			[fieldName]: selectedValue,
		}));

		// Validate based on test type
		if (selectedTestType === "IELTS") {
			const validateScore = (score: string, field: string) => {
				const numScore = Number(score);
				const isValid =
					!isNaN(numScore) &&
					numScore >= 4 &&
					numScore <= 10 &&
					(numScore % 1 === 0 || numScore % 1 === 0.5);

				if (!isValid && score !== "") {
					setErrors((prev) => ({
						...prev,
						[field]:
							"Score must be between 4 and 10, with only .5 decimals allowed",
					}));
					return false;
				}
				return true;
			};

			// Only validate the current field being changed
			if (fieldName.toLowerCase().includes("score")) {
				if (!validateScore(selectedValue, fieldName)) {
					return;
				}
			}
		}

		if (selectedTestType === "PTE") {
			const validatePteScore = (score: string, field: string) => {
				const numScore = Number(score);
				const isValid = !isNaN(numScore) && numScore >= 10 && numScore <= 90;

				if (!isValid && score !== "") {
					setErrors((prev) => ({
						...prev,
						[field]: "Score must be between 10 and 90",
					}));
					return false;
				}
				return true;
			};

			// Only validate the current field being changed
			if (fieldName.toLowerCase().includes("score")) {
				if (!validatePteScore(selectedValue, fieldName)) {
					return;
				}
			}
		}

		if (selectedTestType === "TOEFL") {
			const validateToeflScore = (score: string, field: string) => {
				const numScore = Number(score);

				// Validate only section scores, not overallScore
				if (
					field !== "overallScore" &&
					score !== "" &&
					(isNaN(numScore) || numScore < 0 || numScore > 30)
				) {
					setErrors((prev) => ({
						...prev,
						[field]: "Score must be between 0 and 30",
					}));
					return false;
				}
				return true;
			};

			// Only validate the current field being changed
			if (fieldName.toLowerCase().includes("score")) {
				if (!validateToeflScore(selectedValue, fieldName)) {
					return;
				}

				// Special handling for overall score in TOEFL
				if (fieldName === "overallScore" && selectedValue !== "") {
					const totalScore =
						Number(userEnglishProficiencyDetails.writingScore || 0) +
						Number(userEnglishProficiencyDetails.readingScore || 0) +
						Number(userEnglishProficiencyDetails.listeningScore || 0) +
						Number(userEnglishProficiencyDetails.speakingScore || 0);

					// Check if overallScore matches the sum of all sections
					if (totalScore !== Number(selectedValue)) {
						setErrors((prev) => ({
							...prev,
							overallScore: "Overall score must equal the sum of all sections",
						}));
					} else {
						setErrors((prev) => ({
							...prev,
							overallScore: "", // Clear error if valid
						}));
					}
				}
			}
		}

		if (selectedTestType === "Duolingo") {
			const validateDuolingoScore = (score: string, field: string) => {
				const numScore = Number(score);
				const isValid = !isNaN(numScore) && numScore >= 10 && numScore <= 160;

				if (!isValid && score !== "") {
					setErrors((prev) => ({
						...prev,
						[field]: "Score must be between 10 and 160",
					}));
					return false;
				}
				return true;
			};

			// Only validate the current field being changed
			if (fieldName.toLowerCase().includes("score")) {
				if (!validateDuolingoScore(selectedValue, fieldName)) {
					return;
				}
			}
		}

		if (selectedTestType === "I-Don't-Have-Any") {
			const validateEnglishMarks = (score: string, field: string) => {
				const numScore = Number(score);
				const isValid = !isNaN(numScore) && numScore >= 1 && numScore <= 100;

				if (!isValid && score !== "") {
					setErrors((prev) => ({
						...prev,
						[field]: "Score must be between 1 and 100",
					}));
					return false;
				}

				setErrors((prev) => ({
					...prev,
					[field]: "",
				}));
				return true;
			};

			if (fieldName === "englishMarks") {
				if (!validateEnglishMarks(selectedValue, "englishScore")) {
					return;
				}
			}
		}

		setHasChanged(true);
	};

	const getFieldKey = (
		title: string,
	): keyof typeof userEnglishProficiencyDetails => {
		const key = title
			.split(" ")
			.map((word, index) =>
				index === 0
					? word.toLowerCase()
					: word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
			)
			.join("") as keyof typeof userEnglishProficiencyDetails;
		return key;
	};

	const onSubmitHandler = () => {
		if (allValue) {
			// Save to Redux store
			dispatch(setCourseAcademicDetails(academicDetails));
			dispatch(
				setCourseEnglishProficiencyDetails({
					...userEnglishProficiencyDetails,
					test: selectedTestType,
				}),
			);

			// Perform backend save for global eligibility state
			const normalize = (val: string) => {
				if (!val) return "";
				const v = val
					.toLowerCase()
					.trim()
					.replace(/['']s$/, "");
				if (v === "bachelor") return "bachelor";
				if (v === "master") return "master";
				return v;
			};

			const normalizeGrading = (grading: string) => {
				if (grading === "CGPA") return "CGPA out of 10";
				return grading;
			};

			const sourcePastQualification = academicDetails?.pastQualification || "";
			const resolvedTestName = selectedTestType || "";
			const normalizedTestName = String(resolvedTestName)
				.toLowerCase()
				.replace(/[^a-z]/g, "");
			const isNoEnglishTest = normalizedTestName === "idonthaveany";

			const eligibilitySearchParams = {
				pastQualification: capitalFirstLetter(sourcePastQualification),
				courseStream: academicDetails?.stream || "",
				pastQualificationPercentage: convertToPercentage(
					academicDetails?.pastGrades || "",
					"Percentage",
				),
				qualificationYear: academicDetails?.passingYear || "",
				twelthPercentage: "",
				twelfthPercentage: "",
				state: "",
				...(normalize(sourcePastQualification) === "bachelor"
					? { universityName: "" }
					: {}),
				...(isNoEnglishTest
					? {
						test: "",
						boardName: userEnglishProficiencyDetails.selectBoard || "",
						overall: userEnglishProficiencyDetails.englishMarks || "",
					}
					: {
						test: resolvedTestName,
						listening: userEnglishProficiencyDetails.listeningScore || "",
						reading: userEnglishProficiencyDetails.readingScore || "",
						writing: userEnglishProficiencyDetails.writingScore || "",
						speaking: userEnglishProficiencyDetails.speakingScore || "",
						overall: userEnglishProficiencyDetails.overallScore || "",
					}),
			};

			const applySave = async () => {
				try {
					if (!isAnonymous && userDetails?.user_id) {
						const sendData = { ...eligibilitySearchParams };
						if (!areObjectsEqual(savedEligibility, sendData)) {
							await dispatch(
								saveEligibilityDetails({
									eligibility: sendData,
									studentId: userDetails.user_id,
								}),
							).unwrap();
						}
					} else {
						dispatch(
							saveEligibilityAnonymous({
								...eligibilitySearchParams,
							}),
						);
					}
				} catch (err) {
					console.error(
						"Failed saving eligibility on CheckEligibilityModal modal save",
						err,
					);
				}
			};

			// Fire and forget or await doesn't drastically matter since it's just closing the modal,
			// but await guarantees store has it before background results re-trigger
			applySave().then(() => {
				setIsOpen(false);
				setEligibilityCriteriaPopUp(true);
			});
		} else {
			// Display alert if form is incomplete
			alert("Please fill all required fields before proceeding.");
		}
	};

	// Update stream options based on qualification
	useEffect(() => {
		if (academicDetails?.pastQualification) {
			const selectStreamOptions = streamOptions.filter(
				(item) => item.qualification === academicDetails.pastQualification,
			);

			setSelectedStream(selectStreamOptions[0]?.options || []);
		} else {
			setSelectedStream([]);
		}

		if (academicDetails?.stream === "") {
			setAcademicDetails((prev) => ({
				...prev,
				stream: "",
			}));
		}
	}, [academicDetails?.pastQualification, academicDetails?.stream]);

	useEffect(() => {
		if (!savedEligibility) return;

		const apiData = savedEligibility;
		const normalizePercentageForDisplay = (value: unknown): string => {
			if (value === null || value === undefined || value === "") return "";
			const num = Number(value);
			if (Number.isNaN(num)) return String(value);
			// API may return decimal fraction (e.g. 0.9) for 90%
			if (num > 0 && num <= 1) return String(num * 100);
			return String(num);
		};

		const hasTest = !!apiData.test;

		const englishDetails = hasTest
			? {
				test: apiData.test?.toString() ?? "",
				selectBoard: "",
				englishMarks: "",
				writingScore: apiData.writing?.toString() ?? "",
				readingScore: apiData.reading?.toString() ?? "",
				listeningScore: apiData.listening?.toString() ?? "",
				speakingScore: apiData.speaking?.toString() ?? "",
				overallScore: apiData.overall?.toString() ?? "",
			}
			: {
				test: apiData.boardName || apiData.overall ? "I-Don't-Have-Any" : "",
				selectBoard: apiData.boardName?.toString() ?? "",
				englishMarks: apiData.overall?.toString() ?? "",
				writingScore: "",
				readingScore: "",
				listeningScore: "",
				speakingScore: "",
				overallScore: "",
			};

		// If no test key present but boardName/overall exist, use "I-Don't-Have-Any"
		const resolvedTest = apiData.test
			? apiData.test.toString()
			: apiData.boardName || apiData.overall
				? "I-Don't-Have-Any"
				: "";
		if (resolvedTest) {
			setSelectedTestType(resolvedTest);
			setUserEnglishProficiencyDetails((prev) => ({
				...prev,
				test: resolvedTest,
			}));
		}

		const academicDetails = {
			pastQualification: apiData.pastQualification?.toString() ?? "",
			stream: apiData.courseStream?.toString() ?? "",
			passingYear: apiData.qualificationYear?.toString() ?? "",
			gradingSystem: apiData.gradingSystem?.toString() ?? "Percentage",
			// Support both legacy and new key names from eligibility payloads
			pastGrades: normalizePercentageForDisplay(
				apiData.pastGrades ?? apiData.pastQualificationPercentage,
			),
			pastMathsGrades: apiData.pastMathsGrades?.toString() ?? "",
			pastChemistryGrades: apiData.pastChemistryGrades?.toString() ?? "",
			pastPhysicsGrades: apiData.pastPhysicsGrades?.toString() ?? "",
			pastEnglishGrades: apiData.pastEnglishGrades?.toString() ?? "",
		};

		setInitialEnglishProficiencyDetails(englishDetails);
		setHasChanged(false);

		setUserEnglishProficiencyDetails((prev) => ({
			...prev,
			...englishDetails,
		}));
		setAcademicDetails((prev) => ({
			...prev,
			...academicDetails,
		}));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [savedEligibility]);

	const onInputChangeHandler = (
		e: React.ChangeEvent<HTMLInputElement>,
		fieldName: string,
	) => {
		const value = e.target.value;

		// Allow only numbers & decimal up to 2 places
		if (!/^\d*\.?\d{0,2}$/.test(value)) return;

		const numericValue = Number(value);
		const gradingSys =
			academicDetails.gradingSystem === "CGPA" ? "CGPA" : "Percentage";
		const limits = gradingSystemLimits[gradingSys];

		// Clear previous errors for this field
		setErrors((prev) => ({
			...prev,
			[fieldName]: "",
		}));

		// Validate the value with min/max limits
		if (limits && value !== "") {
			if (numericValue > limits.max || numericValue < limits.min) {
				setErrors((prev) => ({
					...prev,
					[fieldName]: `Marks must be between ${limits.min} and ${limits.max}`,
				}));
			}
		}

		// Update the respective field dynamically
		setAcademicDetails((prev) => ({
			...prev,
			[fieldName]: value,
		}));
	};

	// Synchronize error state for pre-filled or updated data
	useEffect(() => {
		const gradingSys =
			academicDetails.gradingSystem === "CGPA" ? "CGPA" : "Percentage";
		if (gradingSys) {
			const limits = gradingSystemLimits[gradingSys];
			const fieldsToValidate = [
				"pastGrades",
				"pastMathsGrades",
				"pastEnglishGrades",
				"pastPhysicsGrades",
				"pastChemistryGrades",
			];

			setErrors((prev) => {
				const newErrors = { ...prev };
				let hasChanges = false;

				fieldsToValidate.forEach((fieldName) => {
					const value = (academicDetails as any)[fieldName];
					if (value) {
						const numericValue = Number(value);
						if (
							limits &&
							(numericValue > limits.max || numericValue < limits.min)
						) {
							const msg = `Marks must be between ${limits.min} and ${limits.max}`;
							if (newErrors[fieldName as keyof typeof errors] !== msg) {
								newErrors[fieldName as keyof typeof errors] = msg;
								hasChanges = true;
							}
						} else if (newErrors[fieldName as keyof typeof errors]) {
							newErrors[fieldName as keyof typeof errors] = "";
							hasChanges = true;
						}
					} else if (newErrors[fieldName as keyof typeof errors]) {
						newErrors[fieldName as keyof typeof errors] = "";
						hasChanges = true;
					}
				});

				return hasChanges ? newErrors : prev;
			});
		}
	}, [
		academicDetails.pastGrades,
		academicDetails.pastMathsGrades,
		academicDetails.pastEnglishGrades,
		academicDetails.pastPhysicsGrades,
		academicDetails.pastChemistryGrades,
		academicDetails.gradingSystem,
	]);

	// Check if all required fields are filled
	useEffect(() => {
		const {
			pastQualification,
			stream,
			passingYear,
			gradingSystem,
			pastGrades,
			pastMathsGrades,
			pastEnglishGrades,
			pastPhysicsGrades,
			pastChemistryGrades,
			board,
		} = academicDetails || {}; // Provide empty object fallback

		// hasNoErrors is already defined below, but wait, let's check it.

		const hasNoErrors =
			errors && Object.values(errors).every((err) => err === "");

		const gradingSys = gradingSystem === "CGPA" ? "CGPA" : "Percentage";
		const limits = gradingSystemLimits[gradingSys];
		const checkInRange = (val: string) => {
			if (!val) return true; // If value is empty, consider it valid for range check (null check handles presence)
			const num = Number(val);
			return limits && num >= limits.min && num <= limits.max;
		};

		const isPastGradesInRange = checkInRange(pastGrades);
		const isMathsInRange = checkInRange(pastMathsGrades);
		const isEnglishInRange = checkInRange(pastEnglishGrades);
		const isPhysicsInRange = checkInRange(pastPhysicsGrades);
		const isChemistryInRange = checkInRange(pastChemistryGrades);

		// Basic academic information validation with null checks
		const isBasicInfoComplete =
			!!pastQualification &&
			((pastQualification === "10th" && !!board) ||
				(pastQualification !== "10th" && !!stream)) &&
			!!passingYear &&
			!!gradingSystem &&
			!!pastGrades &&
			isPastGradesInRange &&
			hasNoErrors;

		// Country-specific validations with null checks
		const isCanadaEligible =
			country === "Canada" && !!pastMathsGrades && isMathsInRange;
		const isAustraliaEligible =
			country === "Australia" &&
			!!pastMathsGrades &&
			isMathsInRange &&
			!!pastEnglishGrades &&
			isEnglishInRange &&
			!!pastPhysicsGrades &&
			isPhysicsInRange &&
			!!pastChemistryGrades &&
			isChemistryInRange;

		// English proficiency validation with null checks
		const englishDetails = userEnglishProficiencyDetails || {};
		const englishMarks = Number(englishDetails.englishMarks);
		const isEnglishMarksInRange =
			selectedTestType !== "I-Don't-Have-Any" ||
			(englishMarks >= 1 && englishMarks <= 100);

		const isEnglishProficiencyComplete =
			selectedTestType !== "I-Don't-Have-Any"
				? !!englishDetails.overallScore
				: !!englishDetails.selectBoard &&
				!!englishDetails.englishMarks &&
				isEnglishMarksInRange;

		// Set overall form validity
		const academicValid =
			isBasicInfoComplete &&
			(country !== "Canada" || isCanadaEligible) &&
			(country !== "Australia" || isAustraliaEligible);

		setAllValue(academicValid && isEnglishProficiencyComplete);
	}, [
		academicDetails,
		country,
		errors,
		userEnglishProficiencyDetails,
		selectedTestType,
	]);
	return (
		<div
			className="rounded-t-3xl overflow-hidden w-full max-w-md md:max-w-[600px] max-h-[90vh] scrollBarHidden relative bg-[#F8F9FF] text-[#1C2233] z-[120] shadow-2xl">
			<div className="relative max-h-[90vh] overflow-y-auto scrollBarHidden min-w-full">
				<div className="px-6 py-[26px] sm:py-[18px] xl:p-12 min-w-full mb-10">
					<p className="text-[#3B82F6] text-[10px] sm:text-xs font-bold tracking-wider uppercase mb-1">
						Eligibility check
					</p>
					<div className="flex items-center justify-between mb-2">
						<p className="text-lg sm:text-xl font-bold text-[#1C2233] ff_poppins">
							Check Eligibility
						</p>
						<span
							onClick={() => {
								setIsOpen(false);
							}}
							className="cursor-pointer size-8 p-2 rounded-full bg-[#EFEFFF] hover:bg-[#E2E2FF] flex items-center justify-center">
							<Close color="#525B73" />
						</span>
					</div>
					{hasChanged && initialEnglishProficiencyDetails && (
						<div className="mb-4 p-3 bg-[#EFF6FF] border border-[#DBEAFE] rounded-2xl flex items-center gap-2">
							<AlertTriangle className="text-[#3B82F6] w-4 h-4" />
							<p className="text-xs text-[#1E40AF] ff_poppins">
								You have modified the extracted English proficiency details.
							</p>
						</div>
					)}
					<div className="flex flex-col sm:flex-row justify-between sm:items-start mb-8 gap-4">
						<p className="max-w-[506px] font-medium ff_poppins text-sm sm:text-base text-[#808A9D]">
							Tell us about yourself, and we&apos;ll match you with the best-fit
							courses
						</p>
						{userDetails && (
							<button
								onClick={() => {
									// Sync from profile
									if (userDetails?.education_details?.length) {
										const normalize = (val: string) =>
											val
												?.toLowerCase?.()
												.trim?.()
												.replace(/['\u2019]s$/, "") || "";
										const getPriority = (title: string) => {
											const opt = qualificationOptions.find(
												(o) => normalize(o.title) === normalize(title),
											);
											return opt?.priority ?? Infinity;
										};
										const profileHighest = userDetails.education_details.reduce(
											(h: any, c: any) =>
												getPriority(c.qualification_level) <
													getPriority(h.qualification_level)
													? c
													: h,
											userDetails.education_details[0],
										);
										if (profileHighest) {
											setAcademicDetails((prev) => ({
												...prev,
												pastQualification:
													profileHighest.qualification_level ||
													prev.pastQualification,
												pastGrades: String(
													profileHighest.cgpa_or_percentage_value ||
													prev.pastGrades,
												),
												stream: profileHighest.field_of_study || prev.stream,
												passingYear:
													profileHighest.completion_year || prev.passingYear,
												gradingSystem:
													profileHighest.grade_type || prev.gradingSystem,
											}));
										}
									}
									if (
										(userDetails?.english_proficiency_test?.length ?? 0) > 0
									) {
										const test = userDetails!.english_proficiency_test![0];
										setUserEnglishProficiencyDetails((prev) => ({
											...prev,
											overallScore: String(test.overall || prev.overallScore),
											readingScore: String(test.reading || prev.readingScore),
											writingScore: String(test.writing || prev.writingScore),
											speakingScore: String(
												test.speaking || prev.speakingScore,
											),
											listeningScore: String(
												test.listening || prev.listeningScore,
											),
											test: test.testName || prev.test,
										}));
										if (test.testName) {
											setSelectedTestType(test.testName);
										}
									}
								}}
								className="text-xs font-semibold text-white bg-[linear-gradient(94.21deg,_#1776F0_3.59%,_#8F3EBC_122.88%)] px-3 py-1.5 rounded-full hover:opacity-90 ff_poppins cursor-pointer shrink-0 transition-opacity shadow-sm">
								Use My Details
							</button>
						)}
					</div>
					<div>
						<div className="flex flex-col gap-4 md:gap-5 xl:gap-6">
							<CustomBlueSelect
								value={academicDetails.pastQualification}
								fieldName={"pastQualification"}
								title={"Select Past Qualification"}
								content={pastQualification}
								onChange={onChangeHandler}
							/>

							{academicDetails.pastQualification === "10th" ? (
								<SelectInput
									value={academicDetails.board}
									fieldName={"board"}
									title={"Select Board"}
									content={schoolBoard}
									onChange={onChangeHandler}
								/>
							) : academicDetails.pastQualification === "12th" ? (
								<>
									<CustomBlueSelect
										disable={selectedStream.length === 0}
										value={academicDetails.stream}
										fieldName={"stream"}
										title={"Select your stream"}
										content={selectedStream}
										onChange={onChangeHandler}
									/>
								</>
							) : (
								// Show only SelectInput for other qualifications
								<CustomBlueSelect
									disable={selectedStream.length === 0}
									value={academicDetails.stream}
									fieldName={"stream"}
									title={"Select your stream"}
									content={selectedStream}
									onChange={onChangeHandler}
								/>
							)}

							<CustomBlueSelect
								disable={selectedStream.length === 0}
								value={academicDetails.passingYear}
								fieldName={"passingYear"}
								title={"Select Passing Year"}
								content={passingYear}
								onChange={onChangeHandler}
							/>

							<div>
								<label
									className={`text-[#1C2233] text-xs ff_poppins font-semibold`}>
									Enter marks %
								</label>
								<input
									value={academicDetails.pastGrades}
									required
									placeholder="Enter marks %"
									type="number"
									step="0.01"
									onChange={(e) => onInputChangeHandler(e, "pastGrades")}
									className="w-full border-2 border-[#3777ff] bg-white px-3 py-[10px] text-[#3777ff] ff_poppins text-base md:text-lg lg:text-xl leading-[30px] rounded-[8px] active:outline-hidden focus:outline-hidden placeholder:text-base"
								/>
								<span
									className={`absolute -bottom-5 left-0 text-red-500 text-sm ff_poppins ${errors.pastGrades ? "opacity-100" : "opacity-0"
										}`}>
									{errors.pastGrades}
								</span>
							</div>

							{/* Conditional fields for Canada */}
							{country === "Canada" && (
								<div className="relative">
									<input
										value={academicDetails.pastMathsGrades}
										required
										placeholder="Enter 12th Maths marks"
										type="number"
										step="0.01"
										onChange={(e) => onInputChangeHandler(e, "pastMathsGrades")}
										className="w-full px-3 py-[10px] placeholder:text-white bg-[#3777FF8A] text-white ff_poppins text-base md:text-lg lg:text-xl leading-[30px] rounded-[8px] active:outline-hidden focus:outline-hidden placeholder:text-base"
									/>
									<span
										className={`absolute -bottom-5 left-0 text-red-500 text-sm ${errors.pastMathsGrades ? "opacity-100" : "opacity-0"
											}`}>
										{errors.pastMathsGrades}
									</span>
								</div>
							)}

							{/* Conditional fields for Australia */}
							{country === "Australia" && (
								<>
									<div className="relative">
										<input
											value={academicDetails.pastMathsGrades}
											required
											placeholder="Enter 12th Maths marks"
											type="number"
											step="0.01"
											onChange={(e) =>
												onInputChangeHandler(e, "pastMathsGrades")
											}
											className="w-full px-3 py-[10px] placeholder:text-white bg-[#3777FF8A] text-white ff_poppins text-base md:text-lg lg:text-xl leading-[30px] rounded-[8px] active:outline-hidden focus:outline-hidden"
										/>
										<span
											className={`absolute -bottom-5 left-0 text-red-500 text-sm ${errors.pastMathsGrades ? "opacity-100" : "opacity-0"
												}`}>
											{errors.pastMathsGrades}
										</span>
									</div>

									<div className="relative">
										<input
											value={academicDetails.pastEnglishGrades}
											required
											placeholder="Enter 12th English marks"
											type="number"
											step="0.01"
											onChange={(e) =>
												onInputChangeHandler(e, "pastEnglishGrades")
											}
											className="w-full px-3 py-[10px] placeholder:text-white bg-[#3777FF8A] text-white ff_poppins text-base md:text-lg lg:text-xl leading-[30px] rounded-[8px] active:outline-hidden focus:outline-hidden"
										/>
										<span
											className={`absolute -bottom-5 left-0 text-red-500 text-sm ${errors.pastEnglishGrades ? "opacity-100" : "opacity-0"
												}`}>
											{errors.pastEnglishGrades}
										</span>
									</div>

									<div className="relative">
										<input
											value={academicDetails.pastPhysicsGrades}
											required
											placeholder="Enter 12th Physics marks"
											type="number"
											step="0.01"
											onChange={(e) =>
												onInputChangeHandler(e, "pastPhysicsGrades")
											}
											className="w-full px-3 py-[10px] placeholder:text-white bg-[#3777FF8A] text-white ff_poppins text-base md:text-lg lg:text-xl leading-[30px] rounded-[8px] active:outline-hidden focus:outline-hidden"
										/>
										<span
											className={`absolute -bottom-5 left-0 text-red-500 text-sm ${errors.pastPhysicsGrades ? "opacity-100" : "opacity-0"
												}`}>
											{errors.pastPhysicsGrades}
										</span>
									</div>

									<div className="relative">
										<input
											value={academicDetails.pastChemistryGrades}
											required
											placeholder="Enter 12th Chemistry marks"
											type="number"
											step="0.01"
											onChange={(e) =>
												onInputChangeHandler(e, "pastChemistryGrades")
											}
											className="w-full px-3 py-[10px] placeholder:text-white bg-[#3777FF8A] text-white ff_poppins text-base md:text-lg lg:text-xl leading-[30px] rounded-[8px] active:outline-hidden focus:outline-hidden"
										/>
										<span
											className={`absolute -bottom-5 left-0 text-red-500 text-sm ${errors.pastChemistryGrades ? "opacity-100" : "opacity-0"
												}`}>
											{errors.pastChemistryGrades}
										</span>
									</div>
								</>
							)}
						</div>

						<div className="flex flex-col gap-6 xl:gap-6 mt-8 md:mt-10 lg:mt-11 xl:mt-12">
							<p className="max-w-[506px] font-semibold ff_poppins text-sm sm:text-base text-[#1C2233]">
								English Proficiency Details
							</p>
							<CustomBlueSelect
								value={
									selectedTestType &&
									selectedTestType
										.split("-")
										.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
										.join(" ")
								}
								fieldName={"selectTestType"}
								title={"Select test type"}
								content={englishProficiencyDetails}
								onChange={onSelectHandler}
							/>

							{englishProficiencyDetails
								.filter(
									(item) =>
										item.title.replace(/\s+/g, "-") === selectedTestType,
								)
								.map((options) => (
									<div
										key={options.title}
										className="flex items-center gap-6 w-full mx-auto max-w-[1365px] flex-wrap">
										{options.options
											.filter(
												(field) =>
													field.title === "Overall Score" ||
													field.title === "Select board" ||
													field.title === "English Marks"
											)
											.map((field) => (
												<div
													key={field.id}
													className="w-full relative sm:mb-8 xl:mb-0">
													{field.options ? (
														options.title === "I Don't Have Any" ? (
															<SelectInput
																value={userEnglishProficiencyDetails.selectBoard}
																fieldName={`board`}
																title={`Select board`}
																content={schoolBoard}
																onChange={(value) => {
																	onChangeInputHandler(
																		value,
																		getFieldKey(`Select board`),
																	);
																}}
															/>
														) : (
															// Default CustomBlueSelect
															<CustomBlueSelect
																value={
																	userEnglishProficiencyDetails[
																	getFieldKey(field.title)
																	] || ""
																}
																fieldName={field.title}
																title={field.title}
																content={field.options}
																onChange={(value) => {
																	onChangeInputHandler(
																		value,
																		getFieldKey(field.title),
																	);
																}}
															/>
														)
													) : (
														// Default input stays the same
														<input
															value={
																userEnglishProficiencyDetails[
																getFieldKey(field.title)
																] || ""
															}
															onChange={(e) => {
																onChangeInputHandler(
																	e.target.value,
																	getFieldKey(field.title),
																);
															}}
															required={field.title === "Overall Score" || field.title === "English Marks"}
															placeholder={`Enter ${field.title}`}
															type="text"
															className="w-full border-2 border-[#3777ff] bg-white px-3 py-[10px] text-[#3777ff] ff_poppins text-base md:text-lg lg:text-xl leading-[30px] rounded-[8px] active:outline-hidden focus:outline-hidden placeholder:text-base"
														/>
													)}
													{field.title !== "Select board" &&
														field.title !== "English Marks" && (
															<p className="text-red-500 ff_poppins text-xs sm:text-sm md:text-base font-medium mt-1">
																{
																	errors[
																	(field.title.toLowerCase().split(" ")[0] +
																		"Score") as keyof typeof errors
																	]
																}
															</p>
														)}
												</div>
											))}
									</div>
								))}
						</div>
					</div>
				</div>
				<div className="flex z-100 sticky bottom-0 left-0 w-full bg-[#F8F9FF] items-center justify-center gap-3 sm:gap-6 px-[18px] xl:px-8 py-4 border-t border-[#E2E8F0] shadow-[0px_-4px_20px_0px_#0000001F]">
					<button
						onClick={() => {
							setIsOpen(false);
						}}
						className="text-[#3B82F6] font-semibold text-sm px-6 py-2.5 rounded-full border border-[#3B82F6] hover:bg-[#F0F6FF] transition-colors">
						Discard
					</button>
					<button
						onClick={onSubmitHandler}
						type="submit"
						className={`font-semibold ff_poppins text-white text-sm md:text-base py-2.5 px-8 rounded-full bg-[#3B82F6] hover:bg-[#2563EB] shadow-md text-center transition duration-300 ${allValue
							? "hover:scale-105 ease-linear"
							: "opacity-50 cursor-not-allowed"
							}`}
						disabled={!allValue}>
						Save
					</button>
				</div>
			</div>
		</div>
	);
};

export default CheckEligibilityModal;
